import * as THREE from 'three';
import { SceneNode } from '../core/SceneNode';
import { Vector3, Quaternion, Vector2 } from '../types';
import { SceneSettingsData } from '../core/SceneSettingsData';
import { Material } from '../materials/Material';
import { MaterialFactory, MaterialConfiguration, MaterialSet } from '../materials/MaterialFactory';
import { Scene } from '../core/Scene';
import { UVSurfaceMapper } from '../utils/UVSurfaceMapper';
import { ThreeGeomFactory } from './ThreeGeomFactory';

// Type-only import to avoid circular dependency
import type { POI } from '../poi/POI';
import { Logger } from '../core/Logger';
import { PBRMaterial } from '../materials/PBRMaterial';
import { ImageMaterial } from '../materials/ImageMaterial';
import { VideoMaterial } from '../materials/VideoMaterial';
import { getCanonicalShapeName } from './ShapeTypes';

/**
 * Abstract base class for mesh animations
 * Subclasses implement specific animation behaviors
 */
export abstract class Mesh3DAnimator {
    protected _mesh3D: Mesh3D;

    constructor(mesh3D: Mesh3D) {
        this._mesh3D = mesh3D;
    }

    /**
     * Update animations every frame - implemented by subclasses
     */
    public abstract update(deltaTime: number): void;
}

export abstract class Mesh3D extends SceneNode {
    // The POI data model that drives this mesh's properties.
    public readonly poi: POI;

    // Geometry data, managed by subclasses
    private _geometryData: any;

    // 🎯 Clean OOP: Direct material instances, no wrappers
    public primaryMaterial!: Material;
    public secondaryMaterial: Material | null = null;

    public billboardEnabled: boolean = false;
    
    // 🎯 ANIMATOR: null by default, subclasses create their own if needed
    public shapeAnimator: Mesh3DAnimator | null = null;
    
    // Three.js objects - this is where Three.js lives!
    protected _geometry: THREE.BufferGeometry | null = null;
    private _boundingSphereDebugMesh: THREE.Mesh | null = null;
    private _boundingBoxDebugMesh: THREE.Box3Helper | null = null;
    private _transformAxisLines: THREE.Group | null = null;
    
    // Frustum culling support - removed duplicate, uses SceneNode._boundingBox
    private _needsBoundingBoxUpdate: boolean = true;

    constructor(
        poi: POI, 
        protected sceneSettings?: SceneSettingsData, 
        protected appSettings?: any,
        geometryData: any = {}
    ) {
        super(poi.name ?? 'Mesh');
        
        this.poi = poi;
        this._geometryData = geometryData;
        
        // Initialize Three.js objects
        this.buildMesh();

        // Link our scene node to the Three.js object for raycasting
        if (this._mesh) {
            this._mesh.userData.sceneNode = this;
            // Initial calculation of the bounding box
            this.computeInitialBoundingBox();
        }
    }

    /**
     * This method is responsible for creating and assigning the correct geometry
     * and material(s) to the underlying THREE.Mesh.
     */
    protected buildMesh(): void {
        //Logger.Instance.Debug(`[Mesh3D] ${this.name}: Starting buildMesh()`);
        
        // 🎯 Create materials FIRST - some geometries need material data (like aspectRatio)
        const materialSet = MaterialFactory.createMaterialsForPOI(this.poi);
        
        // 🎯 Store MaterialSet for access by texture loading system
        (this as any)._materialSet = materialSet;
        
        // 🎯 CLEAN: Use structured material assignment
        this.primaryMaterial = materialSet.front;
        this.secondaryMaterial = materialSet.sides || null; // Fallback for single-material shapes
        
        //console.log(`🎯 [Mesh3D] ${this.name}: Received MaterialSet from factory`);
        //console.log(`  📦 front: ${!!materialSet.front} (${materialSet.front?.constructor.name})`);
        //console.log(`  📦 back: ${!!materialSet.back} (${materialSet.back?.constructor.name || 'undefined'})`);
        //console.log(`  📦 sides: ${!!materialSet.sides} (${materialSet.sides?.constructor.name || 'undefined'})`);
        if (materialSet.front && (materialSet.front as any).uri) {
            //console.log(`  🎯 front URI: '${(materialSet.front as any).uri.value}'`);
        }
        if (materialSet.back && (materialSet.back as any).uri) {
            //console.log(`  🎯 back URI: '${(materialSet.back as any).uri.value}'`);
        }
        
        // The geometry is created after materials (some shapes need material data)
        this._geometry = this.createGeometry();
        //Logger.Instance.Debug(`[Mesh3D] ${this.name}: Created geometry with ${this._geometry?.getAttribute('position')?.count || 0} vertices`);

        // --- VALIDATION ---
        // If the geometry is invalid (e.g., no vertices), create a dummy box to prevent crashes.
        if (!this._geometry || !this._geometry.getAttribute('position') || this._geometry.getAttribute('position').count === 0) {
            console.warn(`Mesh3D '${this.name}': Invalid geometry created for shape type '${this.getShapeType()}'. Associated POI may have invalid dimensions. Using a placeholder geometry to prevent a crash.`);
            this._geometry = new THREE.BoxGeometry(0.001, 0.001, 0.001);
        }

        // Sanitize geometry position buffer to avoid NaNs later during frustum culling
        this._validateAndSanitizeGeometryPositions();

        // 🎯 CLEAN: Build materials array and assign geometry groups based on MaterialSet
        const materials: THREE.Material[] = [materialSet.front.getThreeMaterial()];
        
        if (materialSet.back) {
            materials.push(materialSet.back.getThreeMaterial());
            //console.log(`🔍 [Mesh3D] Added BACK material at index ${materials.length - 1}`);
        }
        if (materialSet.sides) {
            materials.push(materialSet.sides.getThreeMaterial());
            //console.log(`🔍 [Mesh3D] Added SIDES material at index ${materials.length - 1}`);
        }
        
        const threeMaterials = materials.length > 1 ? materials : materials[0];
        //console.log(`🎯 [Mesh3D] ${this.name}: Using ${materials.length} materials`);
        //console.log(`🔍 [Mesh3D] Material setup: front=${!!materialSet.front}, back=${!!materialSet.back}, sides=${!!materialSet.sides}`);
        
        // 🔍 DEBUG: Check if THREE.js materials have the correct textures
        //console.log(`🔍 [Mesh3D] THREE.js Material Array Check:`);
        if (Array.isArray(threeMaterials)) {
            threeMaterials.forEach((mat, i) => {
                const hasMap = !!(mat as any).map;
                const mapSrc = hasMap ? (mat as any).map?.image?.src : 'none';
                const mapLoaded = hasMap ? (mat as any).map?.image?.complete : false;
                //console.log(`  THREE.Material[${i}]: hasMap=${hasMap}, loaded=${mapLoaded}, src=${mapSrc}`);
            });
            
            // 🕒 Check materials again after 2 seconds
            setTimeout(() => {
                //console.log(`🕒 [Mesh3D] ${this.name}: Materials after 2 seconds:`);
                threeMaterials.forEach((mat, i) => {
                    const hasMap = !!(mat as any).map;
                    const mapSrc = hasMap ? (mat as any).map?.image?.src?.split('/').pop() : 'none';
                    //console.log(`  🕒 Material[${i}]: ${hasMap ? '🖼️' : '🎨'} ${mapSrc}`);
                });
                
                // 🔍 CRITICAL: Re-check actual geometry group assignments
                this._geometry?.groups.forEach((group, i) => {
                    const materialIndex = group.materialIndex ?? 0;
                    const usedMaterial = Array.isArray(threeMaterials) ? threeMaterials[materialIndex] : threeMaterials;
                    if (!usedMaterial) return; // defensive: material may have been disposed/replaced
                    const hasTexture = !!(usedMaterial as any).map;
                    const textureFile = hasTexture ? (usedMaterial as any).map?.image?.src?.split('/').pop() : 'solid';
                    void i; void textureFile;
                });
            }, 2000);
        }

        // 🎯 Assign geometry groups based on actual materials we have
        this.assignMaterialGroups(materialSet);

        // 🚨 CHECK: Are materialIndex values still there after assignment?
        //console.log(`🔍 [Mesh3D] AFTER assignMaterialGroups - checking groups:`);
        this._geometry.groups.forEach((group, i) => {
            //console.log(`  Group[${i}]: materialIndex=${group.materialIndex}`);
        });

        // Create mesh with geometry and the material(s)
        this._mesh = new THREE.Mesh(this._geometry, threeMaterials);
        
        // 🚨 CHECK: Are materialIndex values still there after THREE.Mesh creation?
        //console.log(`🔍 [Mesh3D] AFTER THREE.Mesh creation - checking groups:`);
        (this._mesh as THREE.Mesh).geometry.groups.forEach((group: any, i: number) => {
            //console.log(`  Group[${i}]: materialIndex=${group.materialIndex}`);
        });
        this._mesh.castShadow = true;
        this._mesh.receiveShadow = true;
        
        // 🔍 DEBUG: Log THREE.js mesh details
        //console.log(`🔍 [Mesh3D] THREE.js Mesh created:`);
        //console.log(`  📦 Geometry groups: ${this._geometry.groups.length}`);
        this._geometry.groups.forEach((group, i) => {
            //console.log(`    Group[${i}]: start=${group.start}, count=${group.count}, materialIndex=${group.materialIndex}`);
        });
        //console.log(`  🎨 Materials array: ${Array.isArray(threeMaterials) ? `[${threeMaterials.length} materials]` : 'single material'}`);
        if (Array.isArray(threeMaterials)) {
            threeMaterials.forEach((mat, i) => {
                const hasMap = !!(mat as any).map;
                const textureUuid = (mat as any).map?.uuid || 'none';
                const materialUuid = mat.uuid;
                const textureSrc = (mat as any).map?.image?.src;
                const textureFilename = textureSrc ? textureSrc.split('/').pop() : 'none';
                //console.log(`    🎯 Material[${i}]: ${hasMap ? '🖼️' : '🎨'} ${textureFilename} (uuid=${materialUuid.slice(0,8)})`);
            });
            
            // 🔍 CRITICAL: Debug which materials are actually used by which geometry groups
            //console.log(`  🔗 Geometry Group → Material Mapping:`);
            this._geometry.groups.forEach((group, i) => {
                const materialIndex = group.materialIndex ?? 0;
                const usedMaterial = threeMaterials[materialIndex];
                const hasTexture = !!(usedMaterial as any).map;
                const textureFile = hasTexture ? (usedMaterial as any).map?.image?.src?.split('/').pop() : 'solid';
                //console.log(`    Group[${i}] (faces=${Math.round(group.count/3)}) → Material[${materialIndex}] = ${textureFile}`);
            });
        }
        //Logger.Instance.Debug(`[Mesh3D] ${this.name}: Created THREE.Mesh successfully`);
        
        // Set the main three.js object for the scene graph
        if (this._mesh) {
            // matrixAutoUpdate is managed by SceneNode.syncTransformToThreeJS()
            this.finalizeMeshSetup();
            //Logger.Instance.Debug(`[Mesh3D] ${this.name}: Mesh setup completed`);
        }
    }

    /**
     * 🎯 Assign material groups to geometry based on MaterialSet
     */
    private assignMaterialGroups(materialSet: MaterialSet): void {
        if (!this._geometry || !this._geometry.groups || this._geometry.groups.length === 0) {
            //console.log(`❌ [Mesh3D] No geometry groups to assign materials to`);
            return;
        }

        const useBacksideSideMaterial = this.poi.poidata.useBacksideSideMaterial?.value ?? false;
        //console.log(`🚨 [Mesh3D] assignMaterialGroups() CALLED for ${this.poi.name}!`);
        //console.log(`🔍 [Mesh3D] ${this.poi.name} assignMaterialGroups:`);
        //console.log(`  📋 MaterialSet: front=${!!materialSet.front}, back=${!!materialSet.back}, sides=${!!materialSet.sides}`);
        //console.log(`  🎛️ useBacksideSideMaterial: ${useBacksideSideMaterial}`);
        //console.log(`  📐 Geometry groups: ${this._geometry.groups.length}`);
        //console.log(`  🎯 Shape class: ${this.constructor.name}`);
        
        // 🎯 DELEGATE to subclasses for shape-specific material group assignment
        //console.log(`🔧 [Mesh3D] Calling ${this.constructor.name}.assignMaterialGroupsToGeometry()`);
        this.assignMaterialGroupsToGeometry(materialSet);
        //console.log(`✅ [Mesh3D] ${this.constructor.name}.assignMaterialGroupsToGeometry() completed`);
        
    }

    /**
     * 🖼️ NEW: Check if the material result is a layered material
     */
    private isLayeredMaterial(materials: any): boolean {
        return materials && typeof materials === 'object' && materials.__isLayeredMaterial === true;
    }

    /**
     * 🖼️ NEW: Create layered mesh for background color support
     */
    private createLayeredMesh(layeredMaterial: any): void {
        if (!this._geometry) {
            console.error('Mesh3D: Cannot create layered mesh without geometry');
            return;
        }

        const { baseMaterial, overlayMaterial, poi } = layeredMaterial;

        // Create base mesh with solid color (all faces)
        const baseMesh = new THREE.Mesh(this._geometry, baseMaterial);
        baseMesh.castShadow = true;
        baseMesh.receiveShadow = true;

        // Create overlay geometry (2D plane for front face)
        const overlayGeometry = this.createOverlayPlaneGeometry();
        if (!overlayGeometry) {
            console.error('Mesh3D: Failed to create overlay geometry');
            this._mesh = baseMesh; // Fallback to base mesh only
            return;
        }
        
        // Create overlay mesh with transparent texture
        const overlayMesh = new THREE.Mesh(overlayGeometry, overlayMaterial);
        overlayMesh.castShadow = false; // Don't cast shadows for overlay
        overlayMesh.receiveShadow = false;
        
        // Position overlay on the front face of the object
        this.positionOverlayMesh(overlayMesh);

        // Create a group to hold both meshes
        const meshGroup = new THREE.Group();
        meshGroup.add(baseMesh);
        meshGroup.add(overlayMesh);
        
        this._mesh = meshGroup as any; // Treat the group as our main mesh
    }

    /**
     * 🖼️ NEW: Create a 2D overlay geometry that matches the base shape
     */
    private createOverlayPlaneGeometry(): THREE.BufferGeometry | null {
        // Get the shape type to create matching overlay
        const shapeType = this.poi.getShapeType();
        const canonicalShape = this.poi.poidata.shape.value ?? 'box';
        
        // Get common parameters
        const aspect = this.primaryMaterial.aspectRatio.value ?? 1.0;
        const width = 1 * aspect;
        const height = 1;
        const cornerRadius = this.poi.poidata.cornerRadius?.value ?? 0.0;
        const resolution = 1.0; // Use basic resolution for overlay
        
        // Create 2D version of the same shape
        switch (canonicalShape) {
            case 'box':
            case 'quader':
            case 'tile':
            case 'roundedBox':
                // For box-like shapes, use rounded plane
                if (cornerRadius > 0) {
                    const relativeRadius = cornerRadius / Math.max(width, height);
                    return ThreeGeomFactory.createRoundedPlane(width, height, relativeRadius, resolution);
                } else {
                    return new THREE.PlaneGeometry(width, height);
                }
                
            case 'triangle':
                // Create 2D triangle using ShapeGeometry
                return this.create2DTriangle(width, height, cornerRadius, resolution);
                
            case 'hexagon':
                // Create 2D hexagon using ShapeGeometry
                return this.create2DHexagon(0.5, cornerRadius, resolution);
                
            case 'sphere':
                // For sphere, use circular plane
                return new THREE.CircleGeometry(0.5, Math.max(8, Math.ceil(16 * resolution)));
                
            case 'cylinder':
                // For cylinder, use circular plane
                return new THREE.CircleGeometry(0.5, Math.max(8, Math.ceil(16 * resolution)));
                
            case 'torus':
                // For torus, use ring geometry
                return new THREE.RingGeometry(0.2, 0.5, Math.max(8, Math.ceil(16 * resolution)));
                
            case 'plane':
                // Already a plane, use rounded plane
                if (cornerRadius > 0) {
                    const relativeRadius = cornerRadius / Math.max(width, height);
                    return ThreeGeomFactory.createRoundedPlane(width, height, relativeRadius, resolution);
                } else {
                    return new THREE.PlaneGeometry(width, height);
                }
                
            default:
                // Fallback to simple plane
                return new THREE.PlaneGeometry(width, height);
        }
    }

    /**
     * 🖼️ NEW: Create 2D triangle shape
     */
    private create2DTriangle(width: number, height: number, cornerRadius: number, resolution: number): THREE.BufferGeometry {
        const shape = new THREE.Shape();
        
        // Triangle vertices (equilateral, pointing up)
        const size = Math.min(width, height);
        const h = size * Math.sqrt(3) / 2; // Height of equilateral triangle
        
        const p1 = new THREE.Vector2(0, h / 3);           // Top
        const p2 = new THREE.Vector2(-size / 2, -h / 3);  // Bottom left
        const p3 = new THREE.Vector2(size / 2, -h / 3);   // Bottom right
        
        if (cornerRadius <= 0.0001) {
            // Sharp corners
            shape.moveTo(p1.x, p1.y);
            shape.lineTo(p2.x, p2.y);
            shape.lineTo(p3.x, p3.y);
            shape.closePath();
        } else {
            // Rounded corners (simplified version)
            const t = Math.min(cornerRadius, 0.2); // Clamp radius
            
            const p1a = new THREE.Vector2().lerpVectors(p1, p3, t);
            const p1b = new THREE.Vector2().lerpVectors(p1, p2, t);
            const p2a = new THREE.Vector2().lerpVectors(p2, p1, t);
            const p2b = new THREE.Vector2().lerpVectors(p2, p3, t);
            const p3a = new THREE.Vector2().lerpVectors(p3, p2, t);
            const p3b = new THREE.Vector2().lerpVectors(p3, p1, t);
            
            shape.moveTo(p1b.x, p1b.y);
            shape.quadraticCurveTo(p1.x, p1.y, p1a.x, p1a.y);
            shape.lineTo(p3b.x, p3b.y);
            shape.quadraticCurveTo(p3.x, p3.y, p3a.x, p3a.y);
            shape.lineTo(p2b.x, p2b.y);
            shape.quadraticCurveTo(p2.x, p2.y, p2a.x, p2a.y);
            shape.closePath();
        }
        
        const segments = Math.max(3, Math.ceil(12 * resolution));
        const geometry = new THREE.ShapeGeometry(shape, segments);
        
        // Manual UV mapping for triangle
        const pos = geometry.attributes.position;
        const uv = new Float32Array(pos.count * 2);
        
        for (let i = 0; i < pos.count; i++) {
            const x = pos.getX(i);
            const y = pos.getY(i);
            uv[i * 2] = (x / size) + 0.5;
            uv[i * 2 + 1] = (y / h) + 0.5;
        }
        
        geometry.setAttribute('uv', new THREE.BufferAttribute(uv, 2));
        return geometry;
    }

    /**
     * 🖼️ NEW: Create 2D hexagon shape
     */
    private create2DHexagon(size: number, cornerRadius: number, resolution: number): THREE.BufferGeometry {
        const shape = new THREE.Shape();
        const sides = 6;
        const angleStep = (Math.PI * 2) / sides;
        
        if (cornerRadius <= 0.0001) {
            // Sharp corners
            for (let i = 0; i < sides; i++) {
                const angle = i * angleStep - Math.PI / 2; // Start from top
                const x = Math.cos(angle) * size;
                const y = Math.sin(angle) * size;
                
                if (i === 0) {
                    shape.moveTo(x, y);
                } else {
                    shape.lineTo(x, y);
                }
            }
            shape.closePath();
        } else {
            // Rounded corners (simplified)
            const vertices = [];
            for (let i = 0; i < sides; i++) {
                const angle = i * angleStep - Math.PI / 2;
                vertices.push(new THREE.Vector2(
                    Math.cos(angle) * size,
                    Math.sin(angle) * size
                ));
            }
            
            const t = Math.min(cornerRadius, 0.2);
            shape.moveTo(vertices[0].x, vertices[0].y);
            
            for (let i = 1; i < sides; i++) {
                const prev = vertices[i - 1];
                const curr = vertices[i];
                const next = vertices[(i + 1) % sides];
                
                const p1 = new THREE.Vector2().lerpVectors(curr, prev, t);
                const p2 = new THREE.Vector2().lerpVectors(curr, next, t);
                
                shape.lineTo(p1.x, p1.y);
                shape.quadraticCurveTo(curr.x, curr.y, p2.x, p2.y);
            }
            
            shape.closePath();
        }
        
        const segments = Math.max(3, Math.ceil(12 * resolution));
        const geometry = new THREE.ShapeGeometry(shape, segments);
        
        // Manual UV mapping for hexagon
        const pos = geometry.attributes.position;
        const uv = new Float32Array(pos.count * 2);
        
        for (let i = 0; i < pos.count; i++) {
            const x = pos.getX(i);
            const y = pos.getY(i);
            uv[i * 2] = (x / (size * 2)) + 0.5;
            uv[i * 2 + 1] = (y / (size * 2)) + 0.5;
        }
        
        geometry.setAttribute('uv', new THREE.BufferAttribute(uv, 2));
        return geometry;
    }

    /**
     * 🖼️ NEW: Position overlay mesh on the front face of the object
     */
    private positionOverlayMesh(overlayMesh: THREE.Mesh): void {
        if (!this._geometry) return;

        // Compute the depth of the base geometry to position overlay on front face
        if (!this._geometry.boundingBox) {
            this._geometry.computeBoundingBox();
        }

        if (this._geometry.boundingBox) {
            // Get the Z extent of the base geometry
            const depth = this._geometry.boundingBox.max.z - this._geometry.boundingBox.min.z;
            // Position overlay at the front face plus a small offset
            const frontZ = this._geometry.boundingBox.max.z + 0.001;
            overlayMesh.position.set(0, 0, frontZ);
        } else {
            // Fallback: assume unit cube depth
            overlayMesh.position.set(0, 0, 0.501);
        }
    }

    /**
     * 🖼️ NEW: Compute initial bounding box for both regular meshes and layered meshes
     */
    private computeInitialBoundingBox(): void {
        if (!this._mesh) return;

        if (this._mesh instanceof THREE.Group) {
            // For layered rendering (Group), compute bounding box from the base mesh
            const baseMesh = this._mesh.children[0] as THREE.Mesh;
            if (baseMesh && baseMesh.geometry) {
                if (!baseMesh.geometry.boundingBox) {
                    baseMesh.geometry.computeBoundingBox();
                }
            }
        } else if (this._mesh instanceof THREE.Mesh) {
            // For regular meshes, compute bounding box normally
            if (!this._mesh.geometry.boundingBox) {
                this._mesh.geometry.computeBoundingBox();
            }
        }
    }

    /**
     * Abstract method to be implemented by all concrete shape subclasses.
     * This is the core of the polymorphic geometry creation.
     */
    protected abstract createGeometry(): THREE.BufferGeometry;

    /**
     * 🎯 CLEAN OOP: Assign material groups to geometry faces
     * Override in subclasses for shape-specific logic
     */
    protected assignMaterialGroupsToGeometry(materialSet: MaterialSet): void {
        if (!this._geometry || !this._geometry.groups || this._geometry.groups.length === 0) {
            //console.log(`❌ [${this.constructor.name}] No geometry groups to assign materials to`);
            return;
        }

        // DEFAULT: Simple assignment for most shapes
        // Subclasses can override for complex face assignment
        if (materialSet.back && materialSet.sides) {
            // 3 materials: front, back, sides
            for (let i = 0; i < this._geometry.groups.length; i++) {
                if (i === 0) this._geometry.groups[i].materialIndex = 0; // Front
                else if (i === 1) this._geometry.groups[i].materialIndex = 1; // Back
                else this._geometry.groups[i].materialIndex = 2; // Sides
            }
            //console.log(`🎯 [${this.constructor.name}] DEFAULT 3-material assignment: Front=0, Back=1, Sides=2`);
        } else if (materialSet.sides) {
            // 2 materials: front, sides
            const useBacksideSideMaterial = this.poi.poidata.useBacksideSideMaterial?.value ?? false;
            for (let i = 0; i < this._geometry.groups.length; i++) {
                if (i === 0) this._geometry.groups[i].materialIndex = 0; // Front
                else this._geometry.groups[i].materialIndex = useBacksideSideMaterial ? 1 : 0; // Back/Sides
            }
            //console.log(`🎯 [${this.constructor.name}] DEFAULT 2-material assignment: Front=0, Back/Sides=${useBacksideSideMaterial ? 1 : 0}`);
        } else {
            // 1 material: all faces same
            for (let i = 0; i < this._geometry.groups.length; i++) {
                this._geometry.groups[i].materialIndex = 0;
            }
            //console.log(`🎯 [${this.constructor.name}] DEFAULT 1-material assignment: All=0`);
        }
    }

    /**
     * This method is called by the engine on every frame.
     * It's the entry point for all time-based updates for this node.
     * @param deltaTime The time elapsed since the last frame, in seconds.
     */
    protected override onUpdate(deltaTime: number): void {
        // Performance tracking
        const perfStart = performance.now();

        
        // 🎯 UPDATE: Shape animations (billboard, rotation, wiggle)
        if (this.shapeAnimator) {
            this.shapeAnimator.update(deltaTime);
        }
        
        // Track material updates
        const materialStart = performance.now();
        
        // 🎯 CLEAN: Update primary material wrapper
        this.primaryMaterial?.onUpdate(deltaTime);
        
        // 🎯 CLEAN: Update secondary material wrapper if it exists
        if (this.secondaryMaterial) {
            this.secondaryMaterial.onUpdate(deltaTime);
        }
        
        const _materialTime = performance.now() - materialStart;

        // Transform sync now handled by SceneNode.syncTransformToThreeJS()
        // which runs before onUpdate() in the update() cycle.

        // Handle debug visualizations
        if (this.appSettings) {
            const show = this.appSettings.showBounds.value;
            if (this._boundingSphereDebugMesh) this._boundingSphereDebugMesh.visible = show;
            if (this._boundingBoxDebugMesh) this._boundingBoxDebugMesh.visible = show;
            if (this._transformAxisLines) this._transformAxisLines.visible = show;
        }

        // Sync SceneNode visibility to THREE.js mesh (generic for all meshes)
        if (this._mesh && this._mesh.visible !== this.visible) {
            this._mesh.visible = this.visible;
        }

        // Allow parent class to perform its own updates (e.g., for transform interpolation)
        super.onUpdate(deltaTime);

        // Log performance if enabled
        const totalTime = performance.now() - perfStart;
        if (totalTime > 1.0) { // Only log if update takes more than 1ms
            ////console.log(`⚠️ Slow Mesh3D update: ${totalTime.toFixed(2)}ms (material: ${_materialTime.toFixed(2)}ms, transform: ${_transformTime.toFixed(2)}ms) - ${this.poi.name}`);
        }
    }

    // --- Legacy compatibility methods ---
    
    public getGeometryData(): any {
        return this._geometryData;
    }

    public setGeometryData(data: any): void {
        this._geometryData = data;
        this.recreateGeometry();
    }

    public getMaterialData(): any {
        // This is now deprecated, material data is on the material itself.
        return this.primaryMaterial.serialize ? this.primaryMaterial.serialize() : {};
    }

    public setMaterialData(_data: any): void {
        // This is now deprecated.
        console.warn("setMaterialData is deprecated. Modify material properties directly.");
    }

    // Convenience accessors should be removed in favor of direct access.
    // e.g. myMesh.material.opacity.value
    
    // --- Geometry and State Management ---

    protected recreateGeometry(): void {
        if (!this._mesh) return;

        if (this._mesh instanceof THREE.Group) {
            // For layered rendering, we need to recreate both meshes
            this.recreateLayeredGeometry();
        } else if (this._mesh instanceof THREE.Mesh) {
            // For regular meshes, recreate as before
            this._mesh.geometry.dispose();
            this._mesh.geometry = this.createGeometry();
            this._geometry = this._mesh.geometry;
            this._validateAndSanitizeGeometryPositions();

            // Update the material using existing materials
            let threeMaterials: THREE.Material | THREE.Material[];
            if (this.secondaryMaterial) {
                threeMaterials = [this.primaryMaterial.getThreeMaterial(), this.secondaryMaterial.getThreeMaterial()];
            } else {
                threeMaterials = this.primaryMaterial.getThreeMaterial();
            }
            this._mesh.material = threeMaterials;

            this.onGeometryChanged();
            this.finalizeMeshSetup(); // Use the new helper for consistent debug mesh creation
        }
    }

    /**
     * 🖼️ NEW: Recreate geometry for layered rendering
     */
    private recreateLayeredGeometry(): void {
        if (!(this._mesh instanceof THREE.Group)) return;

        // Dispose old geometries
        this._mesh.children.forEach(child => {
            if (child instanceof THREE.Mesh && child.geometry) {
                child.geometry.dispose();
            }
        });

        // Clear the group
        this._mesh.clear();

        // Recreate the base geometry
        this._geometry = this.createGeometry();
        if (!this._geometry) return;
        this._validateAndSanitizeGeometryPositions();

        // Use existing materials instead of recreating
        let threeMaterials: THREE.Material | THREE.Material[] = this.primaryMaterial.getThreeMaterial();
        
        // Check if we still need layered rendering
        if (this.isLayeredMaterial(threeMaterials)) {
            const layeredMaterial = threeMaterials as any;
            const { baseMaterial, overlayMaterial } = layeredMaterial;

            // Create new base mesh
            const baseMesh = new THREE.Mesh(this._geometry, baseMaterial);
            baseMesh.castShadow = true;
            baseMesh.receiveShadow = true;

            // Create new overlay geometry and mesh
            const overlayGeometry = this.createOverlayPlaneGeometry();
            if (overlayGeometry) {
                const overlayMesh = new THREE.Mesh(overlayGeometry, overlayMaterial);
                overlayMesh.castShadow = false;
                overlayMesh.receiveShadow = false;
                this.positionOverlayMesh(overlayMesh);

                // Add both to the group
                this._mesh.add(baseMesh);
                this._mesh.add(overlayMesh);
            } else {
                // Fallback to base mesh only
                this._mesh.add(baseMesh);
            }
        } else {
            // No longer needs layered rendering, convert back to single mesh
            // This is a complex case - for now, just rebuild the entire mesh
            this.buildMesh();
            return;
        }

        this.onGeometryChanged();
        this.finalizeMeshSetup();
    }
    
    private onGeometryChanged(): void {
        this._needsBoundingBoxUpdate = true;
    }

    /**
     * Validates and sanitizes the current geometry's position attribute to avoid NaN values
     * that would cause THREE.BufferGeometry.computeBoundingSphere() to produce NaN radii.
     * Returns true if a usable position buffer exists after sanitation.
     */
    protected _validateAndSanitizeGeometryPositions(): boolean {
        if (!this._geometry) return false;
        const positionAttr: any = (this._geometry as any).getAttribute('position');
        if (!positionAttr) return false;
        const array: any = positionAttr.array;
        if (!array || array.length === 0) return false;

        let foundInvalid = false;
        for (let i = 0; i < array.length; i++) {
            const value = array[i];
            if (!Number.isFinite(value)) {
                array[i] = 0;
                foundInvalid = true;
            }
        }

        if (foundInvalid) {
            positionAttr.needsUpdate = true;
            if ((this._geometry as any).getAttribute('normal')) {
                (this._geometry as any).computeVertexNormals();
            }
        }

        return true;
    }

    protected _createBoundingVolumeDebugMeshes(): void {
        if (!this._mesh || !this._geometry) return;

        // --- Remove old debug meshes ---
        if (this._boundingSphereDebugMesh) this._mesh.remove(this._boundingSphereDebugMesh);
        if (this._boundingBoxDebugMesh) this._mesh.remove(this._boundingBoxDebugMesh);
        if (this._transformAxisLines) this._mesh.remove(this._transformAxisLines);

        // Validate geometry positions to avoid NaNs during bounding computations
        if (!this._validateAndSanitizeGeometryPositions()) {
            return;
        }

        // Force complete matrix update - critical for geometry changes
        this._mesh.updateMatrix();
        this._mesh.updateMatrixWorld(true);
        
        // Force geometry bounds recalculation (safe after validation)
        this._geometry.computeBoundingBox();
        this._geometry.computeBoundingSphere();

        // --- Calculate bounds consistently for all shapes ---
        // Temporarily store existing children and remove them for clean bounds calculation
        const children = [...this._mesh.children];
        this._mesh.children.length = 0;
        
        const actualBox = new THREE.Box3().setFromObject(this._mesh);
        
        // Restore children
        this._mesh.children.push(...children);
        
        // --- Bounding Sphere (from actual mesh bounds) ---
        const sphere = actualBox.getBoundingSphere(new THREE.Sphere());
        const safeRadius = Number.isFinite(sphere.radius) && sphere.radius > 0 ? sphere.radius : 1e-6;
        const sphereGeom = new THREE.SphereGeometry(safeRadius, 16, 16);
        const sphereMat = new THREE.MeshBasicMaterial({ color: 0xff00ff, wireframe: true, transparent: true, opacity: 0.5 });
        this._boundingSphereDebugMesh = new THREE.Mesh(sphereGeom, sphereMat);
        
        // 🎯 FIX: Compensate for parent non-uniform scaling to keep sphere perfectly round
        // Get the parent's scale to compensate
        const parentScale = this._mesh.scale;
        const invScaleX = parentScale.x !== 0 ? 1 / parentScale.x : 1;
        const invScaleY = parentScale.y !== 0 ? 1 / parentScale.y : 1;
        const invScaleZ = parentScale.z !== 0 ? 1 / parentScale.z : 1;
        
        // Apply inverse scaling to counteract parent's non-uniform scaling
        this._boundingSphereDebugMesh.scale.set(invScaleX, invScaleY, invScaleZ);
        this._boundingSphereDebugMesh.position.copy(sphere.center);
        
        // --- Bounding Box (from actual mesh bounds) ---
        this._boundingBoxDebugMesh = new THREE.Box3Helper(actualBox, 0x00ffff); // Cyan color for box

        // --- Transform Orientation Lines ---
        this._transformAxisLines = new THREE.Group();
        const axisLength = Math.max(0.5, safeRadius * 1.5);
        
        // Get transform matrix for axis directions
        const transform = this._mesh.matrixWorld;
        const origin = new THREE.Vector3().setFromMatrixPosition(transform);
        
        // Extract axis vectors from transform matrix
        const right = new THREE.Vector3().setFromMatrixColumn(transform, 0).normalize().multiplyScalar(axisLength);
        const up = new THREE.Vector3().setFromMatrixColumn(transform, 1).normalize().multiplyScalar(axisLength);
        const front = new THREE.Vector3().setFromMatrixColumn(transform, 2).normalize().multiplyScalar(axisLength); // Positive Z direction - testing if this is the correct front
        
        // Create lines for each axis
        const createAxisLine = (direction: THREE.Vector3, color: number) => {
            const points = [new THREE.Vector3(0, 0, 0), direction];
            const geometry = new THREE.BufferGeometry().setFromPoints(points);
            const material = new THREE.LineBasicMaterial({ color: color, linewidth: 2 });
            return new THREE.Line(geometry, material);
        };
        
        const rightLine = createAxisLine(right, 0xff0000);  // Red for right
        const upLine = createAxisLine(up, 0x00ff00);       // Green for up  
        const frontLine = createAxisLine(front, 0x0000ff); // Blue for front
        
        this._transformAxisLines.add(rightLine);
        this._transformAxisLines.add(upLine);
        this._transformAxisLines.add(frontLine);
        
        // Position the axis lines at the object's world position
        this._transformAxisLines.position.copy(origin);

        // --- Disable Raycasting on Helpers ---
        this._boundingSphereDebugMesh.raycast = () => {};
        this._boundingBoxDebugMesh.raycast = () => {};
        this._transformAxisLines.raycast = () => {};

        // --- Add to scene and set visibility ---
        const show = this.appSettings?.showBounds.value ?? false;
        this._boundingSphereDebugMesh.visible = show;
        this._boundingBoxDebugMesh.visible = show;
        this._transformAxisLines.visible = show;

        this._mesh.add(this._boundingSphereDebugMesh);
        this._mesh.add(this._boundingBoxDebugMesh);
        this._mesh.add(this._transformAxisLines);
    }

    /**
     * Helper method for child classes to call after setting up their mesh
     * This ensures all mesh types get consistent bounding visualizations
     */
    protected finalizeMeshSetup(): void {
        if (this._mesh) {
            this._mesh.userData.sceneNode = this;
            this._createBoundingVolumeDebugMeshes();
        }
    }

    // Frustum culling and bounding box
    public updateBoundingBox(): void {
        if (!this._mesh) {
            this._needsBoundingBoxUpdate = false;
            return;
        }

        if (this._mesh instanceof THREE.Group) {
            // For layered rendering (Group), use the base mesh for bounding box
            const baseMesh = this._mesh.children[0] as THREE.Mesh;
            if (baseMesh && baseMesh.geometry) {
                if (!baseMesh.geometry.boundingBox) {
                    baseMesh.geometry.computeBoundingBox();
                }
                if (baseMesh.geometry.boundingBox) {
                    this._boundingBox = baseMesh.geometry.boundingBox.clone();
                    if (this._boundingBox) {
                        this._boundingBox.applyMatrix4(baseMesh.matrixWorld);
                    }
                }
            }
        } else if (this._mesh instanceof THREE.Mesh) {
            // For regular meshes, compute bounding box normally
            if (!this._mesh.geometry.boundingBox) {
                this._mesh.geometry.computeBoundingBox();
            }
            if (this._mesh.geometry.boundingBox) {
                this._boundingBox = this._mesh.geometry.boundingBox.clone();
                if (this._boundingBox) {
                    this._boundingBox.applyMatrix4(this._mesh.matrixWorld);
                }
            }
        }
        
        this._needsBoundingBoxUpdate = false;
    }

    public get needsBoundingBoxUpdate(): boolean {
        return this._needsBoundingBoxUpdate;
    }

    // Lifecycle and cloning
    protected onDestroy(): void {
        this._geometry?.dispose();
        // Call dispose hook for subclass-specific cleanup
        this.dispose();
        // The material instance might be shared, so we don't dispose it here.
        super.onDestroy();
    }

    // Dispose hook - override in subclasses that need specific cleanup
    protected dispose(): void {
        // Base implementation does nothing
        // Subclasses override if they have specific resources to clean up
    }

    // Override setVisible to sync with THREE.js mesh immediately
    setVisible(visible: boolean, recursive: boolean = false): void {
        super.setVisible(visible, recursive);
        
        // Sync visibility to THREE.js mesh immediately
        if (this._mesh) {
            this._mesh.visible = visible;
        }
    }

    public clone(): Mesh3D | null {
        console.warn("Cloning is not supported for this object type.");
        return null;
    }

    /**
     * Gets the specific shape type of the underlying geometry.
     * @returns A string representing the shape type (e.g., 'box', 'sphere').
     */
    public getShapeType(): string {
        return this.poi.poidata.shape.value ?? 'unknown';
    }

    /**
     * Get 3D world position from UV coordinates (0-1 range)
     * Uses vertex-based interpolation for accurate texture-to-geometry mapping
     * @param uv - UV coordinates (0-1 range)
     * @returns World position on the mesh surface
     */
    public getPositionFromUV(uv: Vector2): Vector3 {
        const mesh = this._mesh as THREE.Mesh;
        if (!mesh?.geometry) {
            throw new Error(`Mesh geometry not available for ${this.name}`);
        }
        
        try {
            // Get local position from UV using vertex interpolation
            const localPos = UVSurfaceMapper.getPositionFromUV(mesh.geometry, uv);
            
            // Transform to world space
            return this.transform.transformPoint(localPos);
        } catch (error) {
            console.warn(`Failed to get position from UV for ${this.name}:`, error);
            // Fallback to shape center
            return this.transform.getWorldPosition();
        }
    }

    // --- Bounding Box Implementation ---
    protected calculateBoundingBox(): THREE.Box3 {
        if (this._mesh) {
            const mesh = this._mesh as THREE.Mesh;
            // The geometry's boundingBox is in local space, which is what we need.
            if (!mesh.geometry.boundingBox) {
                mesh.geometry.computeBoundingBox();
            }
            return mesh.geometry.boundingBox as THREE.Box3;
        }
        return new THREE.Box3();
    }

    /**
     * Checks if the node's bounding box is inside the camera's view frustum.
     * Uses the SceneNode's consistent bounding box system to avoid flashing.
     */
    public isInFrustum(camera: THREE.Camera): boolean {
        try {
            const frustum = new THREE.Frustum();
            const matrix = new THREE.Matrix4().multiplyMatrices(camera.projectionMatrix, camera.matrixWorldInverse);
            frustum.setFromProjectionMatrix(matrix);
            
            // Use the SceneNode's consistent bounding box system
            const boundingBox = this.getBoundingBox();
            return frustum.intersectsBox(boundingBox);
        } catch (error) {
            console.warn(`Frustum culling error for ${this.name}:`, error);
            // Default to visible if there's an error
            return true;
        }
    }



    /**
     * 🎯 ACTIVATION: Base method for activation logic (override in subclasses)
     */
    public setActive(): void {
        // Default: no action - override in subclasses that need activation logic
    }

    /**
     * 🔄 DEACTIVATION: Base method for deactivation logic (override in subclasses)
     */
    public setInactive(): void {
        // Default: no action - override in subclasses that need deactivation logic
    }
} 